import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  MenuItem,
  Button,
  TextField,
  Snackbar,
  Alert,
  Drawer,
  Tooltip,
  IconButton,
  Skeleton,
  Card,
  CardContent,
  Switch,
  styled,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import Sidebar from "../components/Sidebar";
import {
  processPayroll,
  updatePayroll,
  deletePayroll,
  getPayrollRecords,
  fetchUsers,
  fetchBankAccounts,
  getEmployeesByType,
  sendPayslip,
} from "../api/services";

import AddEarningForm from "../components/AddEarningForm";
import EditEarningDialog from "../components/EditEarningDialog";
import EarningsHistory from "../components/EarningsHistory";

// ---- Shared tokens used only by the main file ----
const fin = {
  primary: "#009db9",
  primaryDark: "#007a8a",
  primarySoft: "#e6f6f9",
  text: "#1f2933",
  muted: "#6b7280",
  border: "#e5e7eb",
  surface: "#f7f9fb",
  success: "#2e7d32",
  successSoft: "#e8f5e9",
  danger: "#d32f2f",
  dangerSoft: "#ffebee",
  warning: "#b26a00",
  warningSoft: "#fff4e5",
  radius: "16px",
  shadow: "0 4px 20px rgba(16, 24, 40, 0.06)",
};

const FinancePageHeader = ({ title }) => (
  <Box sx={{ mb: { xs: 3, md: 4 } }}>
    <Typography
      variant="h4"
      sx={{
        fontWeight: "bold",
        color: fin.text,
        letterSpacing: 0.5,
        fontSize: { xs: "1.5rem", sm: "1.85rem", md: "2.125rem" },
        position: "relative",
        "&::after": {
          content: '""',
          display: "block",
          backgroundColor: fin.primary,
          height: "4px",
          width: { xs: "80px", sm: "100px" },
          position: "absolute",
          left: 0,
          bottom: "-8px",
        },
      }}
    >
      {title}
    </Typography>
  </Box>
);

const FinanceMenuButton = ({ onClick }) => (
  <Box
    sx={{
      display: { xs: "block", md: "none" },
      position: "sticky",
      top: { xs: "64px", sm: "80px" },
      zIndex: 1100,
      mb: 2,
    }}
  >
    <Tooltip title="Menu" arrow>
      <IconButton
        onClick={onClick}
        sx={{
          background: "linear-gradient(135deg, #009db9 0%, #007a8a 100%)",
          color: "#fff",
          borderRadius: "12px",
          p: 1.2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          "&:hover": {
            background: "linear-gradient(135deg, #007a8a 0%, #005b66 100%)",
            boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
          },
          transition: "all 0.3s ease",
        }}
        aria-label="toggle sidebar"
      >
        <MenuIcon sx={{ fontSize: "1.6rem" }} />
      </IconButton>
    </Tooltip>
  </Box>
);

const StatGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
      gap: { xs: 1.5, md: 2 },
      mb: { xs: 2.5, md: 4 },
    }}
  >
    {children}
  </Box>
);

const statTones = {
  primary: { bg: fin.primarySoft, color: fin.primary },
  success: { bg: fin.successSoft, color: fin.success },
  warning: { bg: fin.warningSoft, color: fin.warning },
  danger: { bg: fin.dangerSoft, color: fin.danger },
};

const StatCard = ({ label, value, hint, icon: Icon, tone = "primary" }) => {
  const t = statTones[tone] || statTones.primary;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 2,
        minWidth: 0,
        borderRadius: "14px",
        border: `1px solid ${fin.border}`,
        backgroundColor: "#fff",
        boxShadow: fin.shadow,
      }}
    >
      {Icon && (
        <Box
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: "10px",
            backgroundColor: t.bg,
            color: t.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small" />
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: fin.muted }} noWrap>
          {label}
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            color: fin.text,
            fontSize: { xs: "1.05rem", md: "1.2rem" },
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Typography>
        {hint && (
          <Typography variant="caption" sx={{ color: fin.muted }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ---- Presentational helper used only here ----
const formatKsh = (value) =>
  `KSh ${(Number(value) || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

const EarningsManagement = () => {
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [email, setEmail] = useState("");
  const [selectedBankAccount, setSelectedBankAccount] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [grossPay, setGrossPay] = useState("");
  const [bonus, setBonus] = useState("");
  const [tax, setTax] = useState("");
  const [withholdingTax, setWithholdingTax] = useState("");
  const [housingLevy, setHousingLevy] = useState("");
  const [sha, setSha] = useState("");
  const [nssf, setNssf] = useState("");
  const [paymentDate, setPaymentDate] = useState();
  const [paymentStatus, setPaymentStatus] = useState("");
  const [earningsData, setEarningsData] = useState([]);
  const [filter, setFilter] = useState("All");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalElements, setTotalElements] = useState(0);
  const [users, setUsers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEarning, setSelectedEarning] = useState(null);
  const [actionType, setActionType] = useState("");
  const [editGrossPay, setEditGrossPay] = useState("");
  const [editBonus, setEditBonus] = useState("");
  const [editTax, setEditTax] = useState("");
  const [editWithholdingTax, setEditWithholdingTax] = useState("");
  const [editHousingLevy, setEditHousingLevy] = useState("");
  const [editSha, setEditSha] = useState("");
  const [editNssf, setEditNssf] = useState("");
  const [editPaymentDate, setEditPaymentDate] = useState(null);
  const [editPaymentStatus, setEditPaymentStatus] = useState("");
  const [editSelectedBankAccount, setEditSelectedBankAccount] = useState("");
  const [editUserType, setEditUserType] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isBonusPayment, setIsBonusPayment] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [bankLoading, setBankLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [totals, setTotals] = useState(null);

  const handleApiError = (error, defaultMessage) => {
    console.error("API Error:", error);
    if (error.response?.status === 404) {
      setSnackbarMessage(
        "The requested resource was not found. Please try again."
      );
      setSnackbarSeverity("warning");
    } else if (error.response?.status === 403) {
      setSnackbarMessage(
        "You do not have permission to perform this action. Please contact an administrator."
      );
      setSnackbarSeverity("error");
    } else if (error.response?.status === 500) {
      setSnackbarMessage("Server error. Please try again later.");
      setSnackbarSeverity("error");
    } else if (error.message?.includes("Bank account not found")) {
      setSnackbarMessage(
        "Bank account not found. Please ensure the user has a valid bank account."
      );
      setSnackbarSeverity("warning");
    } else {
      setSnackbarMessage(
        defaultMessage || "An unexpected error occurred. Please try again."
      );
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (selectedUserType) {
          const userData = await getEmployeesByType(
            selectedUserType.toUpperCase()
          );
          setUsers(userData || []);
        } else {
          const userData = await fetchUsers();
          const activeUsers = userData[0]?.activeUsers || [];
          setUsers(activeUsers);
        }
      } catch (error) {
        handleApiError(error, "Error fetching users");
      }
    };
    fetchUserData();
  }, [selectedUserType]);

  useEffect(() => {
    const fetchBankAccountData = async () => {
      if (!selectedUser) {
        setBankAccounts([]);
        setSelectedBankAccount("");
        setEmployeeId("");
        setEmail("");
        return;
      }

      const user = users.find((u) => u.name === selectedUser);
      const userId = user?.id;
      if (!userId) {
        setBankAccounts([]);
        setSelectedBankAccount("");
        return;
      }

      try {
        setBankLoading(true);
        setEmployeeId(user.employeeId || "N/A");
        setEmail(user.email || "N/A");

        const response = await fetchBankAccounts();

        const allBankAccounts = Array.isArray(response)
          ? response
          : response?.data?.content ??
            response?.content ??
            response?.data ??
            [];

        const userBankAccounts = allBankAccounts.filter((a) => {
          if (a.userId && a.userId === userId) return true;
          if (
            user.employeeId &&
            a.employeeId &&
            a.employeeId === user.employeeId
          )
            return true;
          return false;
        });

        const mapped = userBankAccounts.map((a) => ({
          id: a.id,
          bankName: a.bankName,
          accountNumber: a.accountNumber,
          fullName: a.fullName,
          accountType: a.accountType,
          accountCurrency: a.accountCurrency,
          mobileNumber: a.mobileNumber,
          userId: a.userId,
        }));

        setBankAccounts(mapped);

        if (mapped.length === 0) {
          setSnackbarMessage(
            `No bank accounts found for this ${
              selectedUserType || "user"
            }. Please add a bank account first.`
          );
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }

        setSelectedBankAccount(mapped.length === 1 ? mapped[0].id : "");
      } catch (error) {
        setBankAccounts([]);
        setSelectedBankAccount("");
        setEmployeeId("");
        setEmail("");
        handleApiError(error, "Error fetching bank accounts");
      } finally {
        setBankLoading(false);
      }
    };

    fetchBankAccountData();
  }, [selectedUser, users, selectedUserType]);

  const fetchPayrollData = async (pageNum, size) => {
    setHistoryLoading(true);
    try {
      const response = await getPayrollRecords(pageNum, size);
      const records = response.content || response;
      const mapped = records.map((r) => ({
        id: r.id,
        userId: r.bankAccount?.userId ?? "N/A",
        name: r.bankAccount?.fullName ?? "Unknown",
        bankAccount: r.bankAccount
          ? `${r.bankAccount.bankName} (${r.bankAccount.accountNumber})`
          : "No Bank Account",
        bankAccountId: r.bankAccount?.id ?? null,
        grossPay: r.grossPay,
        bonus: r.bonus,
        tax: r.tax ?? 0,
        withholdingTax: r.withholdingTax ?? 0,
        housingLevy: r.housingLevy ?? 0,
        sha: r.sha ?? 0,
        nssf: r.nssf ?? 0,
        paymentDate: r.currentPaymentDate,
        paymentStatus: r.paymentStatus,
        userType: r.userType ?? "Employee",
      }));
      setEarningsData(mapped);
      setTotalElements(response.totalElements || mapped.length);
    } catch (error) {
      handleApiError(error, "Error fetching payroll records");
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchPayrollTotals = async () => {
    try {
      let pageIndex = 0;
      let totalPages = 1;
      let gross = 0;
      let bonusSum = 0;
      do {
        const res = await getPayrollRecords(pageIndex, 100);
        const rows = res.content || res;
        const pageTotals = rows.reduce(
          (totals, r) => ({
            gross: totals.gross + (Number(r.grossPay) || 0),
            bonus: totals.bonus + (Number(r.bonus) || 0),
          }),
          { gross: 0, bonus: 0 }
        );
        gross += pageTotals.gross;
        bonusSum += pageTotals.bonus;
        totalPages = res.totalPages || 1;
        pageIndex += 1;
      } while (pageIndex < totalPages);
      setTotals({
        gross: Math.round(gross * 100) / 100,
        bonus: Math.round(bonusSum * 100) / 100,
      });
    } catch (error) {
      console.error("Error fetching payroll totals:", error);
      setTotals(null);
    }
  };

  useEffect(() => {
    fetchPayrollData(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchPayrollTotals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uniqueEarningNames = [...new Set(earningsData.map((e) => e.name))];

  const filteredData =
    filter === "All"
      ? earningsData
      : earningsData.filter((e) => e.name === filter);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPayrollData(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    fetchPayrollData(0, newSize);
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handlePaymentTypeToggle = (e) => setIsBonusPayment(e.target.checked);

  const handleSubmit = async () => {
    if (!paymentDate || !paymentStatus) {
      setSnackbarMessage("Please fill in payment date and payment status");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    const employeeIdForPayslip = employeeId;

    const payrollData = {
      grossPay: grossPay ? parseFloat(grossPay) : 0,
      bonus: bonus ? parseFloat(bonus) : 0,
      tax: tax ? parseFloat(tax) : 0,
      withholdingTax: withholdingTax ? parseFloat(withholdingTax) : 0,
      housingLevy: housingLevy ? parseFloat(housingLevy) : 0,
      bankAccountId: selectedBankAccount,
      currentPaymentDate: paymentDate.toISOString().split("T")[0],
      paymentStatus: paymentStatus,
      sha: selectedUserType === "Employee" ? (sha ? parseFloat(sha) : 0) : 0,
      nssf: nssf ? parseFloat(nssf) : 0,
    };

    setSubmitting(true);
    try {
      const response = await processPayroll(payrollData);
      if (!response.id) throw new Error("Failed to process payroll");

      fetchPayrollData(page, rowsPerPage);
      fetchPayrollTotals();

      let payslipSent = false;
      let payslipErrorMsg = "";

      if (!employeeIdForPayslip || employeeIdForPayslip === "N/A") {
        payslipErrorMsg = "Employee ID is missing.";
      } else {
        try {
          const payslipResult = await sendPayslip(employeeIdForPayslip);
          console.log("Payslip API result:", payslipResult);
          payslipSent = true;
        } catch (payslipErr) {
          console.error("Payslip send failed:", payslipErr);
          payslipErrorMsg =
            payslipErr?.response?.data?.message ||
            payslipErr?.message ||
            "Failed to send payslip email.";
        }
      }

      if (payslipSent) {
        setSnackbarMessage(
          "Payroll processed and payslip emailed successfully!"
        );
        setSnackbarSeverity("success");
      } else {
        setSnackbarMessage(
          `Payroll processed, but payslip not sent: ${payslipErrorMsg}`
        );
        setSnackbarSeverity("warning");
      }
      setSnackbarOpen(true);

      setSelectedUserType("");
      setSelectedUser("");
      setEmployeeId("");
      setEmail("");
      setSelectedBankAccount("");
      setBankAccounts([]);
      setGrossPay("");
      setBonus("");
      setTax("");
      setWithholdingTax("");
      setHousingLevy("");
      setSha("");
      setNssf("");
      setPaymentDate(new Date());
      setPaymentStatus("");
      setIsBonusPayment(false);
    } catch (error) {
      handleApiError(error, "Error processing payroll");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenModal = async (earning, type) => {
    setSelectedEarning(earning);
    setActionType(type);

    if (type === "Edit") {
      setEditGrossPay(earning.grossPay.toString());
      setEditBonus(earning.bonus.toString());
      setEditTax(earning.tax.toString());
      setEditWithholdingTax(earning.withholdingTax.toString());
      setEditHousingLevy(earning.housingLevy.toString());
      setEditSha(earning.sha.toString());
      setEditNssf(earning.nssf.toString());
      setEditPaymentDate(new Date(earning.paymentDate));
      setEditPaymentStatus(earning.paymentStatus);
      setEditUserType(earning.userType || "Employee");
      setEditSelectedBankAccount(earning.bankAccountId || "");
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedEarning(null);
    setActionType("");
    setEditGrossPay("");
    setEditBonus("");
    setEditTax("");
    setEditWithholdingTax("");
    setEditHousingLevy("");
    setEditSha("");
    setEditNssf("");
    setEditPaymentDate(null);
    setEditPaymentStatus("");
    setEditSelectedBankAccount("");
    setEditUserType("");
  };

  const handleConfirmAction = async () => {
    if (actionType === "Delete") {
      setActionLoading(true);
      try {
        await deletePayroll(selectedEarning.id);
        fetchPayrollData(page, rowsPerPage);
        fetchPayrollTotals();
        setSnackbarMessage("Earning entry deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (error) {
        handleApiError(error, "Error deleting payroll");
      } finally {
        setActionLoading(false);
      }
    } else if (actionType === "Edit") {
      if (!editPaymentDate || !editPaymentStatus) {
        setSnackbarMessage("Please fill in payment date and payment status");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      const bankAccountId =
        selectedEarning?.bankAccountId || editSelectedBankAccount;
      if (!bankAccountId) {
        setSnackbarMessage("Bank account is required for updating payroll.");
        setSnackbarSeverity("warning");
        setSnackbarOpen(true);
        return;
      }

      const updatedPayrollData = {
        grossPay: editGrossPay ? parseFloat(editGrossPay) : 0,
        bonus: editBonus ? parseFloat(editBonus) : 0,
        tax: editTax ? parseFloat(editTax) : 0,
        withholdingTax: editWithholdingTax ? parseFloat(editWithholdingTax) : 0,
        housingLevy: editHousingLevy ? parseFloat(editHousingLevy) : 0,
        bankAccountId,
        currentPaymentDate: editPaymentDate.toISOString().split("T")[0],
        paymentStatus: editPaymentStatus,
        sha:
          editUserType === "Employee" ? (editSha ? parseFloat(editSha) : 0) : 0,
        nssf: editNssf ? parseFloat(editNssf) : 0,
      };

      setActionLoading(true);
      try {
        await updatePayroll(selectedEarning.id, updatedPayrollData);
        fetchPayrollData(page, rowsPerPage);
        fetchPayrollTotals();
        setSnackbarMessage("Earning entry updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } catch (error) {
        handleApiError(error, "Error updating payroll");
      } finally {
        setActionLoading(false);
      }
    }
    handleCloseModal();
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const formatBankAccountDisplay = (account) => {
    if (account.accountNumber && account.mobileNumber)
      return `${account.bankName} (${account.accountNumber}) / ${account.mobileNumber}`;
    if (account.accountNumber)
      return `${account.bankName} (${account.accountNumber})`;
    if (account.mobileNumber) return `Mobile: ${account.mobileNumber}`;
    return "No payment method";
  };

  const bankHelperText = bankLoading
    ? "Loading payment methods..."
    : selectedUser && bankAccounts.length === 0
    ? `No bank accounts found for this ${selectedUserType || "user"}`
    : "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          width: "100%",
          flexGrow: 1,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar />
        </Box>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, md: 4 },
            pt: { xs: "50px", sm: "60px", md: "120px" },
            backgroundColor: "#fff",
            minHeight: "100vh",
            ml: { md: "20px" },
            pr: { xs: 1, md: 3 },
            position: "relative",
          }}
        >
          <FinanceMenuButton onClick={handleDrawerToggle} />

          <FinancePageHeader title="Earnings Management" />

          <StatGrid>
            <StatCard
              label="Total records"
              value={totalElements.toLocaleString()}
              icon={ReceiptLongOutlinedIcon}
            />
            <StatCard
              label="Gross pay"
              value={totals ? formatKsh(totals.gross) : "..."}
              hint="All records"
              icon={PaymentsOutlinedIcon}
              tone="success"
            />
            <StatCard
              label="Bonuses"
              value={totals ? formatKsh(totals.bonus) : "..."}
              hint="All records"
              icon={EmojiEventsOutlinedIcon}
              tone="warning"
            />
          </StatGrid>

          <AddEarningForm
            selectedUserType={selectedUserType}
            setSelectedUserType={setSelectedUserType}
            selectedUser={selectedUser}
            setSelectedUser={setSelectedUser}
            employeeId={employeeId}
            setEmployeeId={setEmployeeId}
            email={email}
            setEmail={setEmail}
            selectedBankAccount={selectedBankAccount}
            setSelectedBankAccount={setSelectedBankAccount}
            bankAccounts={bankAccounts}
            bankLoading={bankLoading}
            bankHelperText={bankHelperText}
            users={users}
            grossPay={grossPay}
            setGrossPay={setGrossPay}
            bonus={bonus}
            setBonus={setBonus}
            tax={tax}
            setTax={setTax}
            withholdingTax={withholdingTax}
            setWithholdingTax={setWithholdingTax}
            housingLevy={housingLevy}
            setHousingLevy={setHousingLevy}
            sha={sha}
            setSha={setSha}
            nssf={nssf}
            setNssf={setNssf}
            paymentDate={paymentDate}
            setPaymentDate={setPaymentDate}
            paymentStatus={paymentStatus}
            setPaymentStatus={setPaymentStatus}
            isBonusPayment={isBonusPayment}
            handlePaymentTypeToggle={handlePaymentTypeToggle}
            handleSubmit={handleSubmit}
            submitting={submitting}
            formatBankAccountDisplay={formatBankAccountDisplay}
          />

          <EarningsHistory
            historyLoading={historyLoading}
            filteredData={filteredData}
            uniqueEarningNames={uniqueEarningNames}
            filter={filter}
            setFilter={setFilter}
            rowsPerPage={rowsPerPage}
            totalElements={totalElements}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleOpenModal={handleOpenModal}
          />
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          zIndex: 1100,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 250,
            top: { xs: "64px", sm: "80px" },
            height: "calc(100% - 64px)",
            borderRight: "none",
          },
        }}
      >
        <Sidebar onItemClick={handleDrawerToggle} />
      </Drawer>

      <EditEarningDialog
        openModal={openModal}
        actionType={actionType}
        actionLoading={actionLoading}
        selectedEarning={selectedEarning}
        handleCloseModal={handleCloseModal}
        handleConfirmAction={handleConfirmAction}
        editUserType={editUserType}
        editGrossPay={editGrossPay}
        setEditGrossPay={setEditGrossPay}
        editBonus={editBonus}
        setEditBonus={setEditBonus}
        editTax={editTax}
        setEditTax={setEditTax}
        editWithholdingTax={editWithholdingTax}
        setEditWithholdingTax={setEditWithholdingTax}
        editHousingLevy={editHousingLevy}
        setEditHousingLevy={setEditHousingLevy}
        editSha={editSha}
        setEditSha={setEditSha}
        editNssf={editNssf}
        setEditNssf={setEditNssf}
        editPaymentDate={editPaymentDate}
        setEditPaymentDate={setEditPaymentDate}
        editPaymentStatus={editPaymentStatus}
        setEditPaymentStatus={setEditPaymentStatus}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "10px" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EarningsManagement;